import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Star, Check, X, Trash2, LogOut, Mail, Calendar, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const { t } = useI18n();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/admin", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(m[1]);
    (async () => {
      try {
        const res = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        navigate("/admin", { replace: true, state: { user: res.data } });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(detail || t.admin.not_authorized);
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, t]);

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAFA]">
      <div className="flex items-center gap-2 text-[#1A1A1A]">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>Loading…</span>
      </div>
    </div>
  );
};

export const AdminPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang, setLang } = useI18n();
  const [authState, setAuthState] = useState(location.state?.user ? "authenticated" : "checking");
  const [user, setUser] = useState(location.state?.user || null);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      if (!res.data.is_admin) {
        setAuthState("not_authorized");
        return;
      }
      setUser(res.data);
      setAuthState("authenticated");
    } catch {
      setAuthState("not_authenticated");
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      // AuthCallback handles it
      return;
    }
    if (authState === "checking") checkAuth();
  }, [authState, checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      // Logout best-effort: clear local state even if server call fails
      console.warn("Logout error:", err?.message || err);
    }
    setUser(null);
    setAuthState("not_authenticated");
    navigate("/", { replace: true });
  };

  if (authState === "checking") {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAFAFA]">
        <Loader2 className="w-6 h-6 animate-spin text-[#1A1A1A]" />
      </div>
    );
  }

  if (authState !== "authenticated") {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAFAFA] px-5">
        <div className="max-w-md w-full bg-white rounded-3xl border border-black/5 shadow-sm p-8 md:p-10 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-[#FFB800] grid place-items-center font-black text-[#1A1A1A]">R&V</div>
          <h1 className="mt-6 text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}>{t.admin.login_title}</h1>
          <p className="mt-3 text-[#4A4A4A] text-sm md:text-base">{t.admin.login_lead}</p>
          {authState === "not_authorized" && (
            <p className="mt-4 text-sm font-semibold text-red-600">{t.admin.not_authorized}</p>
          )}
          <button data-testid="admin-login-btn" onClick={login} className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-[#2D5A27] text-white font-semibold rounded-full px-6 py-3.5 transition-all">
            {t.admin.login_btn}
          </button>
          <div className="mt-5 flex items-center justify-center gap-2">
            <button onClick={() => setLang("it")} className={`text-xs font-bold ${lang === "it" ? "text-[#FFB800]" : "text-[#4A4A4A]"}`}>IT</button>
            <span className="text-[#4A4A4A]/40">·</span>
            <button onClick={() => setLang("en")} className={`text-xs font-bold ${lang === "en" ? "text-[#FFB800]" : "text-[#4A4A4A]"}`}>EN</button>
          </div>
        </div>
      </div>
    );
  }

  return <AdminDashboard user={user} onLogout={logout} />;
};

const AdminDashboard = ({ user, onLogout }) => {
  const { t, lang, setLang } = useI18n();
  const [tab, setTab] = useState("reviews");
  const [reviews, setReviews] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("pending");

  const refresh = useCallback(async () => {
    try {
      const [r, b] = await Promise.all([
        axios.get(`${API}/reviews/all`),
        axios.get(`${API}/bookings`),
      ]);
      setReviews(r.data);
      setBookings(b.data);
    } catch {
      toast.error("Errore di caricamento");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const act = async (id, action) => {
    try {
      if (action === "delete") {
        await axios.delete(`${API}/reviews/${id}`);
      } else {
        await axios.post(`${API}/reviews/${id}/${action}`);
      }
      await refresh();
      toast.success("OK");
    } catch {
      toast.error("Errore");
    }
  };

  const filtered = reviews.filter((r) => r.status === filter);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="sticky top-0 z-30 bg-white border-b border-black/5">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-9 w-9 rounded-full bg-[#FFB800] grid place-items-center font-black text-[#1A1A1A] text-[12px]">R&V</span>
            <div className="leading-tight">
              <div className="font-bold text-sm text-[#1A1A1A]">R&V Villa</div>
              <div className="text-[11px] text-[#4A4A4A]">{t.admin.dashboard} · {user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center rounded-full p-1 border bg-white border-black/10">
              <button onClick={() => setLang("it")} className={`w-9 h-7 text-[11px] font-bold ${lang === "it" ? "bg-[#FFB800] text-[#1A1A1A] rounded-full" : "text-[#4A4A4A]"}`}>IT</button>
              <button onClick={() => setLang("en")} className={`w-9 h-7 text-[11px] font-bold ${lang === "en" ? "bg-[#FFB800] text-[#1A1A1A] rounded-full" : "text-[#4A4A4A]"}`}>EN</button>
            </div>
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1A1A1A] hover:text-[#FFB800]">
              <LogOut className="w-4 h-4" />{t.admin.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-10 py-10">
        <div className="flex items-center gap-2 flex-wrap">
          <TabBtn active={tab === "reviews"} onClick={() => setTab("reviews")}>{t.admin.reviews} <span className="ml-1 text-[10px] opacity-70">({reviews.length})</span></TabBtn>
          <TabBtn active={tab === "bookings"} onClick={() => setTab("bookings")}>{t.admin.bookings} <span className="ml-1 text-[10px] opacity-70">({bookings.length})</span></TabBtn>
        </div>

        {tab === "reviews" && (
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-5 flex-wrap">
              {["pending", "approved", "rejected"].map((s) => (
                <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`} className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase transition-colors ${filter === s ? "bg-[#1A1A1A] text-white" : "bg-white border border-black/10 text-[#1A1A1A] hover:bg-black/5"}`}>
                  {t.admin[s]} ({reviews.filter((r) => r.status === s).length})
                </button>
              ))}
            </div>
            {filtered.length === 0 ? (
              <div className="rounded-3xl bg-white border border-black/5 p-10 text-center text-[#4A4A4A]">{t.admin.no_items}</div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((r) => (
                  <article key={r.id} data-testid={`admin-review-${r.id}`} className="rounded-3xl bg-white border border-black/5 p-6">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-[#1A1A1A]">{r.name}</span>
                      <div className="flex items-center gap-1">{[...Array(5)].map((_, i) => (<Star key={i} className={i < r.rating ? "w-3.5 h-3.5 fill-[#FFB800] text-[#FFB800]" : "w-3.5 h-3.5 text-[#FFB800]/30"} />))}</div>
                    </div>
                    <div className="mt-1 text-[11px] text-[#4A4A4A]">{r.period || ""} · {r.email || "—"}</div>
                    <p className="mt-3 text-sm text-[#1A1A1A] leading-relaxed">"{r.text}"</p>
                    <div className="mt-5 pt-4 border-t border-black/5 flex flex-wrap items-center gap-2">
                      {r.status !== "approved" && <BtnA onClick={() => act(r.id, "approve")}><Check className="w-3.5 h-3.5" />{t.admin.approve}</BtnA>}
                      {r.status !== "rejected" && <BtnR onClick={() => act(r.id, "reject")}><X className="w-3.5 h-3.5" />{t.admin.reject}</BtnR>}
                      <BtnD onClick={() => act(r.id, "delete")}><Trash2 className="w-3.5 h-3.5" />{t.admin.delete}</BtnD>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "bookings" && (
          <div className="mt-6">
            {bookings.length === 0 ? (
              <div className="rounded-3xl bg-white border border-black/5 p-10 text-center text-[#4A4A4A]">{t.admin.no_items}</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-5">
                {bookings.map((b) => (
                  <article key={b.id} data-testid={`admin-booking-${b.id}`} className="rounded-3xl bg-white border border-black/5 p-6">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-[#1A1A1A]">{b.name}</span>
                      <span className="text-[11px] text-[#4A4A4A]">{new Date(b.created_at).toLocaleString()}</span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <Row icon={Mail}>{b.email}</Row>
                      <Row icon={MessageCircle}>{b.phone || "—"}</Row>
                      <Row icon={Calendar}>{b.check_in || "—"} → {b.check_out || "—"}</Row>
                      <Row icon={Star}>{b.guests || "—"} · {b.accommodation || "—"}</Row>
                    </div>
                    {b.message && <p className="mt-3 text-sm text-[#1A1A1A] leading-relaxed border-t border-black/5 pt-3">"{b.message}"</p>}
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

const TabBtn = ({ active, ...rest }) => (
  <button {...rest} className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${active ? "bg-[#1A1A1A] text-white" : "bg-white border border-black/10 text-[#1A1A1A] hover:bg-black/5"}`} />
);
const BtnA = (p) => <button {...p} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#2D5A27] text-white hover:bg-[#244821]" />;
const BtnR = (p) => <button {...p} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-white text-[#1A1A1A] border border-black/10 hover:bg-black/5" />;
const BtnD = (p) => <button {...p} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-red-600 hover:bg-red-50" />;
const Row = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 text-[#4A4A4A]"><Icon className="w-3.5 h-3.5" />{children}</span>
);

export default AdminPage;
